// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { operatingGuideAPI } from '../../../utils/apiClient';
import { Button } from '../../common/Button';
import { OPERATING_GUIDES, DEFAULT_GUIDE_ID } from '../../device/operatingGuide/guides';

interface Props {
  inline?: boolean;
  onClose: () => void;
}

/**
 * SuperAdmin config: pick which operating guide every user of the account sees in
 * the "📖 Operating Guide" tab. The dropdown is built from the frontend guide
 * registry (OPERATING_GUIDES); the chosen id is persisted on the account.
 */
export const OperatingGuideSettings: React.FC<Props> = ({ inline, onClose }) => {
  const { setOperatingGuide: setAuthOperatingGuide } = useAuth();

  // '' represents "client default" (no explicit selection stored).
  const [current, setCurrent] = useState<string>('');
  const [selected, setSelected] = useState<string>('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await operatingGuideAPI.get();
      if (!data.success) throw new Error('Failed to load');
      const id = data.operatingGuide ?? '';
      setCurrent(id);
      setSelected(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the current guide.');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = selected !== current;

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setSaving(true);
      setError(null);
      // Empty string clears the selection (back to the client default).
      const result = await operatingGuideAPI.set(selected || null);
      if (!result.success) throw new Error('Save failed');
      const saved = result.operatingGuide ?? '';
      setCurrent(saved);
      setSelected(saved);
      setAuthOperatingGuide(result.operatingGuide ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const containerStyle: React.CSSProperties = inline
    ? {}
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 'max(20px, 5vh)',
        zIndex: 5000,
      };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '3vmin', fontSize: '2vmin', color: '#666' }}>Loading…</div>
      </div>
    );
  }

  const defaultLabel =
    OPERATING_GUIDES.find((g) => g.id === DEFAULT_GUIDE_ID)?.label ?? DEFAULT_GUIDE_ID;

  return (
    <div style={containerStyle} onClick={inline ? undefined : onClose}>
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          ...(inline ? {} : { maxWidth: '480px', width: '90%', maxHeight: 'min(90vh, 85dvh)' }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: inline ? 'none' : '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        <div
          style={{
            ...(inline ? {} : { overflowY: 'scroll', flex: 1 }),
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>Operating Guide</h2>
            {!inline && (
              <Button
                text="✕"
                onClick={onClose}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                  borderRadius: '0.3vmin',
                }}
              />
            )}
          </div>

          <p style={{ fontSize: '1.6vmin', color: '#666', margin: '0 0 2vmin' }}>
            Choose the operating guide shown to everyone in this account under the “📖 Operating
            Guide” tab.
          </p>

          {/* Selector */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1vmin',
              padding: '1vmin',
              background: '#f9f9f9',
              borderRadius: '0.5vmin',
              border: '1px solid #ddd',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1.6vmin', fontWeight: 'bold' }}>Active guide</div>
              <div style={{ fontSize: '1.3vmin', color: '#888' }}>account.operating_guide</div>
            </div>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                fontSize: '1.6vmin',
                padding: '0.5vmin 1vmin',
                border: hasChanges ? '2px solid #2196F3' : '1px solid #ccc',
                borderRadius: '0.3vmin',
                minWidth: '180px',
              }}
            >
              <option value="">Default ({defaultLabel})</option>
              {OPERATING_GUIDES.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p style={{ color: '#c00', fontSize: '1.6vmin', margin: '1.5vmin 0 0' }}>{error}</p>
          )}

          {hasChanges && (
            <div
              style={{
                marginTop: '1vmin',
                fontSize: '1.4vmin',
                color: '#2196F3',
                fontStyle: 'italic',
              }}
            >
              Unsaved changes
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '1vmin', marginTop: '2vmin' }}>
            <Button
              text={saving ? 'Saving…' : 'Save'}
              onClick={handleSave}
              disabled={saving || !hasChanges}
              style={{
                flex: 1,
                background: hasChanges ? '#4CAF50' : '#ccc',
                color: 'white',
                fontSize: '1.8vmin',
                padding: '1vmin 2vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                opacity: hasChanges && !saving ? 1 : 0.6,
              }}
            />
            {!inline && (
              <Button
                text="Cancel"
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1,
                  background: '#ccc',
                  color: '#333',
                  fontSize: '1.8vmin',
                  padding: '1vmin 2vmin',
                  borderRadius: '0.5vmin',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatingGuideSettings;
